/**
 * @file ui/tables/SmelterListTable.tsx
 * @description 冶炼厂清单表格：支持 lookup 自动填充、行内编辑与行内外部选择。
 */

import { PlusOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons'
import type { SmelterLookupRecord } from '@core/data/lookups'
import type {
  MineralDef,
  SmelterListConfig,
  TemplateType,
  TemplateVersionDef,
} from '@core/registry/types'
import { isSmelterNotIdentified, isSmelterNotListed, normalizeSmelterLookup } from '@core/transform'
import type { SmelterRow } from '@core/types/tableRows'
import type {
  SmelterExternalPickItem,
  SmelterListIntegration,
  SmelterNumberLookupContext,
  SmelterNumberLookupPickContext,
  SmelterRowPickContext,
  SmelterLookupMode,
} from '@lib/public/integrations'
import { renderRequiredHeaderLabel, wrapRequired } from '@ui/helpers/fieldRequired'
import { getReadonlyTextControlProps } from '@ui/helpers/readonlyDisplay'
import { useHandlerMap } from '@ui/hooks/useHandlerMap'
import { useScopedModal } from '@ui/hooks/useScopedModal'
import { useT } from '@ui/i18n/useT'
import { useCreation, useLatest, useMemoizedFn } from 'ahooks'
import {
  AutoComplete,
  Button,
  Card,
  ConfigProvider,
  Flex,
  Table,
  Select,
  Input,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { TableRowSelection } from 'antd/es/table/interface'
import type { ChangeEvent } from 'react'
import { memo, useState } from 'react'

import {
  buildNewSmelterRowId,
  hasDuplicateSmelterSelectionForMetal,
  mergeExternalSmelterPickIntoRow,
  resolveExternalSmelterMetal,
  shouldDisableSmelterFieldsAfterExternalPick,
} from './smelterExternalNormalize'
import { getSmelterHeaderProfile, type SmelterColumnId } from './smelterHeaderProfiles'

interface SmelterListTableProps {
  templateType: TemplateType
  versionId: string
  versionDef: TemplateVersionDef
  config: SmelterListConfig
  availableMetals: Array<MineralDef & { label?: string }>
  rows: SmelterRow[]
  onChange: (rows: SmelterRow[]) => void
  countryOptions?: Array<{ value: string; label: string }>
  smelterLookupRecords?: Record<string, SmelterLookupRecord>
  smelterLookupMeta?: {
    notListed: string
    notYetIdentified: string
  }
  showNotYetIdentifiedCountryHint?: boolean
  integration?: SmelterListIntegration
}

const CUSTOM_SMELTER_ROW_CLASS_NAME = 'cm-smelter-row-custom'

const INPUT_FIELDS = [
  'smelterNumber',
  'smelterName',
  'smelterIdentification',
  'smelterStreet',
  'smelterCity',
  'smelterState',
  'smelterContactName',
  'smelterContactEmail',
  'proposedNextSteps',
  'mineName',
  'comments',
  'combinedSmelter',
] as const

const SELECT_FIELDS = [
  'metal',
  'smelterLookup',
  'smelterCountry',
  'mineCountry',
  'recycledScrap',
  'combinedMetal',
] as const

const SMELTER_COLUMN_KEY_BY_ID: Record<SmelterColumnId, string> = {
  smelterNumberInput: 'smelterNumber',
  metal: 'metal',
  smelterLookup: 'smelterLookup',
  smelterName: 'smelterName',
  smelterCountry: 'smelterCountry',
  smelterIdentification: 'smelterIdentification',
  smelterStreet: 'smelterStreet',
  smelterCity: 'smelterCity',
  smelterState: 'smelterState',
  smelterContactName: 'smelterContactName',
  smelterContactEmail: 'smelterContactEmail',
  proposedNextSteps: 'proposedNextSteps',
  mineName: 'mineName',
  mineCountry: 'mineCountry',
  recycledScrap: 'recycledScrap',
  comments: 'comments',
}

/** 冶炼厂清单表格：支持 lookup 自动填充、行内编辑与行内外部选择。 */
export const SmelterListTable = memo(function SmelterListTable({
  templateType,
  versionId,
  versionDef,
  config,
  availableMetals,
  rows,
  onChange,
  countryOptions = [],
  smelterLookupRecords,
  smelterLookupMeta,
  showNotYetIdentifiedCountryHint = false,
  integration,
}: SmelterListTableProps) {
  const { t, locale } = useT()
  const { componentDisabled } = ConfigProvider.useConfig()
  const modal = useScopedModal()
  /** 批量选择状态（受控）。 */
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const rowsRef = useLatest(rows)
  const [rowPickingId, setRowPickingId] = useState<string | null>(null)
  const [numberLookupRowId, setNumberLookupRowId] = useState<string | null>(null)
  const validSelectedRowKeys = useCreation(() => {
    const valid = new Set(rows.map((r) => r.id))
    return selectedRowKeys.filter((k) => valid.has(k))
  }, [rows, selectedRowKeys])

  /** 行索引缓存：避免频繁全表 map/filter。 */
  const rowIndexMap = useCreation(() => new Map(rows.map((row, index) => [row.id, index])), [rows])

  /** 判断：lookup 选择为“未列出”。 */
  const isNotListed = (value: string) => Boolean(smelterLookupMeta && isSmelterNotListed(value))
  /** 判断：lookup 选择为“尚未识别”。 */
  const isNotYetIdentified = (value: string) =>
    Boolean(smelterLookupMeta && isSmelterNotIdentified(value))
  /** 判断：lookup 值来自已识别记录。 */
  const isFromLookup = (value: string) => {
    if (!value) return false
    if (isNotListed(value) || isNotYetIdentified(value)) return false
    const normalized = normalizeSmelterLookup(value)
    return Boolean(smelterLookupRecords?.[normalized])
  }
  /** “Smelter not listed” 是否要求名称+国家（默认 true）。 */
  const notListedRequiresNameCountry = config.notListedRequireNameCountry ?? true

  /** 计算 “Smelter not yet identified” 的默认国家值。 */
  const resolveNotYetIdentifiedCountry = (metalKey: string) => {
    const byMetal = config.notYetIdentifiedCountryByMetal?.[metalKey]
    if (typeof byMetal === 'string') return byMetal
    return config.notYetIdentifiedCountryDefault ?? 'Unknown'
  }

  // wrapRequired 已提取到 @ui/helpers/fieldRequired
  /** 添加空行（保持字段结构完整）。 */
  const handleAddRow = useMemoizedFn(() => {
    if (componentDisabled) return
    const newRow: SmelterRow = {
      id: buildNewSmelterRowId(),
      metal: '',
      smelterLookup: '',
      smelterName: '',
      smelterCountry: '',
      combinedMetal: '',
      combinedSmelter: '',
      smelterNumber: '',
      smelterIdentification: '',
      sourceId: '',
      smelterStreet: '',
      smelterCity: '',
      smelterState: '',
      smelterContactName: '',
      smelterContactEmail: '',
      proposedNextSteps: '',
      mineName: '',
      mineCountry: '',
      recycledScrap: '',
      comments: '',
    }
    onChange([...rows, newRow])
  })

  const showLoadingIndicator = integration?.showLoadingIndicator ?? false
  const smelterLookupMode: SmelterLookupMode = integration?.lookupMode ?? 'internal'
  const useExternalLookup = smelterLookupMode === 'external' || smelterLookupMode === 'hybrid'

  const getCurrentRowsSnapshot = useMemoizedFn(() => rowsRef.current.map((r) => ({ ...r })))

  const applyExternalPickToRow = useMemoizedFn(
    (params: {
      row: SmelterRow
      partial: SmelterExternalPickItem
      preserveMetal: boolean
    }): SmelterRow => {
      const merged = mergeExternalSmelterPickIntoRow({
        row: params.row,
        partial: params.partial,
        preserveMetal: params.preserveMetal,
      })
      if (!merged.smelterLookup) return merged
      if (!smelterLookupMeta) return merged
      const mergedWithName: SmelterRow =
        !merged.smelterName &&
        !isNotListed(merged.smelterLookup) &&
        !isNotYetIdentified(merged.smelterLookup)
          ? { ...merged, smelterName: merged.smelterLookup }
          : merged
      if (isFromLookup(mergedWithName.smelterLookup)) {
        return applySmelterLookup(mergedWithName, mergedWithName.smelterLookup)
      }
      return mergedWithName
    },
  )

  const updateRowById = useMemoizedFn((id: string, nextRow: SmelterRow) => {
    const currentRows = rowsRef.current
    const index = currentRows.findIndex((r) => r.id === id)
    if (index < 0) return
    const next = currentRows.slice()
    next[index] = nextRow
    onChange(next)
  })

  const handleExternalPickForRow = useMemoizedFn(async (id: string) => {
    if (componentDisabled || !integration?.onPickSmelterForRow || rowPickingId) return
    const currentRows = rowsRef.current
    const row = currentRows.find((r) => r.id === id)
    if (!row || !row.metal) return
    const ctx: SmelterRowPickContext = {
      templateType,
      versionId,
      locale,
      versionDef,
      config,
      currentRows: getCurrentRowsSnapshot(),
      rowId: id,
      row: { ...row },
      metal: row.metal,
    }
    setRowPickingId(id)
    try {
      const result = await integration.onPickSmelterForRow(ctx)
      const picked = result?.items?.[0]
      if (!picked) return
      const pickedRowId = typeof picked.id === 'string' ? picked.id.trim() : ''
      if (!pickedRowId) {
        modal.error({
          title: t('errors.externalPickFailedTitle'),
          content: t('errors.externalPickMissingIdContent'),
        })
        return
      }
      const nextRow = applyExternalPickToRow({
        row,
        partial: picked,
        preserveMetal: true,
      })
      if (
        hasDuplicateSmelterSelectionForMetal({
          currentRows,
          currentRowId: id,
          nextRow,
        })
      ) {
        modal.warning({
          title: t('errors.duplicateSmelterSelectionTitle'),
          content: t('errors.duplicateSmelterSelectionContent'),
        })
        return
      }
      updateRowById(id, nextRow)
    } catch {
      modal.error({
        title: t('errors.externalPickFailedTitle'),
        content: t('errors.externalPickFailedContent'),
      })
    } finally {
      setRowPickingId(null)
    }
  })

  const showSmelterNumberLookupWarning = useMemoizedFn(
    (kind: 'notFound' | 'multiple' | 'outOfScope') => {
      if (kind === 'notFound') {
        modal.warning({
          title: t('errors.smelterNumberLookupNotFoundTitle'),
          content: t('errors.smelterNumberLookupNotFoundContent'),
        })
        return
      }
      if (kind === 'outOfScope') {
        modal.warning({
          title: t('errors.smelterNumberLookupOutOfScopeTitle'),
          content: t('errors.smelterNumberLookupOutOfScopeContent'),
        })
        return
      }
      modal.warning({
        title: t('errors.smelterNumberLookupMultipleTitle'),
        content: t('errors.smelterNumberLookupMultipleContent'),
      })
    },
  )

  const resolveSmelterNumberLookupItem = useMemoizedFn(
    async (
      ctx: SmelterNumberLookupContext,
      items: SmelterExternalPickItem[],
    ): Promise<SmelterExternalPickItem | null> => {
      if (items.length === 0) {
        showSmelterNumberLookupWarning('notFound')
        return null
      }
      if (items.length === 1) return items[0]!
      if (!integration?.onPickSmelterForNumberLookup) {
        showSmelterNumberLookupWarning('multiple')
        return null
      }
      const pickCtx: SmelterNumberLookupPickContext = {
        ...ctx,
        searchField: 'smelterNumber',
        searchValue: ctx.smelterNumber,
        candidates: items.map((item) => ({ ...item })),
      }
      const result = await integration.onPickSmelterForNumberLookup(pickCtx)
      return result?.items?.[0] ?? null
    },
  )

  const normalizeSmelterNumberLookupPick = useMemoizedFn(
    (picked: SmelterExternalPickItem, smelterNumber: string): SmelterExternalPickItem | null => {
      const resolvedMetal = resolveExternalSmelterMetal(picked.metal, availableMetals)
      if (!resolvedMetal.inScope) {
        showSmelterNumberLookupWarning('outOfScope')
        return null
      }
      return {
        ...picked,
        metal: resolvedMetal.value || picked.metal,
        smelterNumber: picked.smelterNumber ?? smelterNumber,
      }
    },
  )

  const handleSmelterNumberLookup = useMemoizedFn(async (id: string) => {
    if (componentDisabled || !integration?.onLookupSmelterByNumber || numberLookupRowId) return
    const currentRows = rowsRef.current
    const row = currentRows.find((r) => r.id === id)
    const smelterNumber = row?.smelterNumber?.trim() ?? ''
    if (!row || !smelterNumber) return
    const ctx: SmelterNumberLookupContext = {
      templateType,
      versionId,
      locale,
      versionDef,
      config,
      currentRows: getCurrentRowsSnapshot(),
      rowId: id,
      row: { ...row },
      smelterNumber,
    }
    setNumberLookupRowId(id)
    try {
      const result = await integration.onLookupSmelterByNumber(ctx)
      const pickedItem = await resolveSmelterNumberLookupItem(ctx, result?.items ?? [])
      if (!pickedItem) return
      const picked = normalizeSmelterNumberLookupPick(pickedItem, smelterNumber)
      if (!picked) return
      const nextRow = applyExternalPickToRow({ row, partial: picked, preserveMetal: false })
      if (hasDuplicateSmelterSelectionForMetal({ currentRows, currentRowId: id, nextRow })) {
        modal.warning({
          title: t('errors.duplicateSmelterSelectionTitle'),
          content: t('errors.duplicateSmelterSelectionContent'),
        })
        return
      }
      updateRowById(id, nextRow)
    } catch {
      modal.error({
        title: t('errors.externalPickFailedTitle'),
        content: t('errors.externalPickFailedContent'),
      })
    } finally {
      setNumberLookupRowId(null)
    }
  })

  /** 删除指定行（基于缓存索引定位）。 */
  const handleRemoveRow = useMemoizedFn((id: string) => {
    if (componentDisabled) return
    const index = rowIndexMap.get(id)
    if (index === undefined) return
    const next = rows.slice()
    next.splice(index, 1)
    onChange(next)
  })

  const handleRemoveRowWithSelection = useMemoizedFn((id: string) => {
    setSelectedRowKeys((prev) => prev.filter((k) => k !== id))
    handleRemoveRow(id)
  })

  /**
   * lookup 变更时同步填充/清空相关字段。
   *
   * 业务逻辑：
   * - "Smelter not listed" → 清空所有 ID/地址字段，要求手动填写名称和国家
   * - "Smelter not yet identified" → 填入 Unknown 值，按 metal 自动匹配国家
   * - 内部 lookup 模式 → 以 lookup 值作为名称
   * - 外部 lookup 模式 → 从 smelterLookupRecords 匹配并填充完整信息
   */
  const applySmelterLookup = (row: SmelterRow, value: string) => {
    const normalizedValue = normalizeSmelterLookup(value)
    const next: SmelterRow = { ...row, smelterLookup: normalizedValue }
    if (!smelterLookupMeta || !value) return next
    if (isSmelterNotListed(normalizedValue)) {
      return {
        ...next,
        smelterNumber: '',
        smelterName: '',
        smelterCountry: '',
        smelterIdentification: '',
        sourceId: '',
        smelterStreet: '',
        smelterCity: '',
        smelterState: '',
      }
    }
    if (isSmelterNotIdentified(normalizedValue)) {
      const country = resolveNotYetIdentifiedCountry(row.metal)
      return {
        ...next,
        smelterName: 'Unknown',
        smelterCountry: country,
        smelterNumber: 'Unknown',
        smelterIdentification: 'Unknown',
        sourceId: '',
        smelterStreet: '',
        smelterCity: '',
        smelterState: '',
      }
    }

    // internal：不再使用内置 smelter lookup 选项；允许 free text 并同步到 Smelter Name。
    if (!useExternalLookup) {
      return {
        ...next,
        smelterName: normalizedValue,
      }
    }

    const record = smelterLookupRecords?.[normalizedValue]
    if (!record) return next
    return {
      ...next,
      smelterName: normalizedValue,
      smelterNumber: record.smelterId,
      smelterIdentification: record.smelterId,
      sourceId: record.sourceId,
      smelterCountry: record.country,
      smelterStreet: record.street,
      smelterCity: record.city,
      smelterState: record.state,
    }
  }

  /**
   * 更新单元格值（lookup / metal 字段有联动逻辑）。
   *
   * 特殊处理：
   * - smelterLookup 变更 → 走 applySmelterLookup 联动
   * - metal 变更 + 当前为 "not yet identified" → 同步更新国家
   * - combinedMetal 变更 → 同时更新 metal
   * - 其它字段 → 直接更新
   */
  const handleCellChange = useMemoizedFn((id: string, field: keyof SmelterRow, value: string) => {
    if (componentDisabled) return
    const index = rowIndexMap.get(id)
    if (index === undefined) return
    const row = rows[index]
    if (!row) return
    const nextRow = (() => {
      if (field === 'smelterLookup') {
        return applySmelterLookup(row, value)
      }
      if (field === 'metal' && isNotYetIdentified(row.smelterLookup)) {
        const country = resolveNotYetIdentifiedCountry(value)
        return {
          ...row,
          metal: value,
          smelterName: row.smelterName || 'Unknown',
          smelterCountry: country,
          smelterNumber: row.smelterNumber || 'Unknown',
          smelterIdentification: row.smelterIdentification || 'Unknown',
        }
      }
      if (row[field] === value) return row
      return { ...row, [field]: value }
    })()
    if (nextRow === row) return
    const next = rows.slice()
    next[index] = nextRow
    onChange(next)
  })

  /** 缓存输入回调，减少表格内联函数开销。 */
  const getInputHandler = useHandlerMap(() => {
    const map = new Map<string, (event: ChangeEvent<HTMLInputElement>) => void>()
    rows.forEach((row) => {
      INPUT_FIELDS.forEach((field) => {
        map.set(`${row.id}:${field}`, (event) =>
          handleCellChange(row.id, field, event.target.value),
        )
      })
    })
    return map
  }, [rows, handleCellChange])

  /** 缓存下拉回调。 */
  const getSelectHandler = useHandlerMap(() => {
    const map = new Map<string, (value: string) => void>()
    rows.forEach((row) => {
      SELECT_FIELDS.forEach((field) => {
        map.set(`${row.id}:${field}`, (value) => handleCellChange(row.id, field, value))
      })
    })
    return map
  }, [rows, handleCellChange])

  /** 缓存删除按钮回调。 */
  const getRemoveHandler = useHandlerMap(() => {
    const map = new Map<string, () => void>()
    rows.forEach((row) => {
      map.set(row.id, () => handleRemoveRowWithSelection(row.id))
    })
    return map
  }, [rows, handleRemoveRowWithSelection])

  /** 批量删除处理 */
  const handleBatchDelete = useMemoizedFn(() => {
    if (componentDisabled) return
    if (validSelectedRowKeys.length === 0) return
    modal.confirm({
      title: t('confirm.batchDelete'),
      content: t('confirm.batchDeleteContent', { count: validSelectedRowKeys.length }),
      okText: t('actions.batchDelete'),
      okType: 'danger',
      cancelText: t('actions.cancelSelection'),
      onOk: () => {
        const selectedSet = new Set(validSelectedRowKeys)
        onChange(rows.filter((row) => !selectedSet.has(row.id)))
        setSelectedRowKeys([])
      },
    })
  })

  /** 取消选择 */
  const handleCancelSelection = useMemoizedFn(() => {
    if (componentDisabled) return
    setSelectedRowKeys([])
  })

  /** 行选择配置 */
  const rowSelection: TableRowSelection<SmelterRow> = {
    selectedRowKeys: validSelectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys as string[]),
  }

  const showEditableActions = !componentDisabled

  const metalOptions = useCreation(
    () =>
      availableMetals.map((m) => ({
        value: m.key,
        label: m.label ?? t(m.labelKey),
      })),
    [availableMetals, t],
  )
  const yesNoUnknownOptions = useCreation(
    () => [
      { value: 'Yes', label: t('options.yes') },
      { value: 'No', label: t('options.no') },
      { value: 'Unknown', label: t('options.unknown') },
    ],
    [t],
  )
  const yesNoOptions = useCreation(
    () => [
      { value: 'Yes', label: t('options.yes') },
      { value: 'No', label: t('options.no') },
    ],
    [t],
  )
  const recycledScrapOptions =
    config.recycledScrapOptions === 'yes-no' ? yesNoOptions : yesNoUnknownOptions
  /** 统一的下拉搜索过滤逻辑，避免多处重复定义。 */
  const filterOptionByLabel = useMemoizedFn((input: string, option?: { label?: string }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
  )
  const isSmelterBaseFieldDisabled = useMemoizedFn((record: SmelterRow) =>
    Boolean(
      componentDisabled ||
      shouldDisableSmelterFieldsAfterExternalPick({
        useExternalLookup,
        row: record,
        fromLookup: isFromLookup(record.smelterLookup),
        notListed: isNotListed(record.smelterLookup),
        notYetIdentified: isNotYetIdentified(record.smelterLookup),
      }),
    ),
  )
  const headerProfile = useCreation(
    () => getSmelterHeaderProfile({ templateType, versionId, locale, t, config }),
    [config, locale, t, templateType, versionId],
  )
  const resolveColumnTitle = useMemoizedFn((columnId: SmelterColumnId) =>
    renderRequiredHeaderLabel(
      headerProfile.labels[columnId],
      headerProfile.required[columnId] === true,
    ),
  )
  // ---------------------------------------------------------------------------
  // 列定义（约 430 行）
  // 按业务逻辑顺序构建：metal → smelterLookup/smelterName → smelterNumber →
  // country → identification → 地址字段 → recycledScrap → comments → 操作
  // 每列的 render 函数负责行内编辑（Input/Select/AutoComplete）和必填标记。
  // ---------------------------------------------------------------------------
  const columns = useCreation<ColumnsType<SmelterRow>>(() => {
    const columns: ColumnsType<SmelterRow> = []

    columns.push({
      title: resolveColumnTitle('metal'),
      dataIndex: 'metal',
      key: 'metal',
      width: 140,
      fixed: 'left',
      render: (value: string, record: SmelterRow) =>
        wrapRequired(
          true,
          <Select
            value={value || undefined}
            onChange={getSelectHandler(`${record.id}:metal`)}
            options={metalOptions}
            disabled={componentDisabled}
            {...getReadonlyTextControlProps({
              value,
              placeholder: t('placeholders.select'),
              disabled: componentDisabled,
              className: 'w-full',
            })}
          />,
          componentDisabled,
        ),
    })

    if (config.hasLookup) {
      columns.push({
        title: resolveColumnTitle('smelterLookup'),
        dataIndex: 'smelterLookup',
        key: 'smelterLookup',
        width: 220,
        fixed: 'left',
        render: (value: string, record: SmelterRow) =>
          wrapRequired(
            Boolean(record.metal),
            useExternalLookup && integration?.onPickSmelterForRow ? (
              <Flex align="center" gap={8}>
                {record.smelterLookup ? (
                  <>
                    <Typography.Text ellipsis title={record.smelterLookup} style={{ maxWidth: 150 }}>
                      {record.smelterLookup}
                    </Typography.Text>
                    {showEditableActions && (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => handleExternalPickForRow(record.id)}
                        disabled={!record.metal || rowPickingId === record.id}
                        loading={showLoadingIndicator && rowPickingId === record.id}
                      >
                        {t('actions.edit')}
                      </Button>
                    )}
                  </>
                ) : showEditableActions ? (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => handleExternalPickForRow(record.id)}
                    disabled={!record.metal || rowPickingId === record.id}
                    loading={showLoadingIndicator && rowPickingId === record.id}
                  >
                    {t('actions.chooseSmelter')}
                  </Button>
                ) : (
                  <Typography.Text type="secondary">-</Typography.Text>
                )}
                {showEditableActions && smelterLookupMode === 'hybrid' && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('actions.pickExternal')}
                  </Typography.Text>
                )}
              </Flex>
            ) : (
              <Flex vertical gap={4}>
                <AutoComplete
                  value={value || undefined}
                  onChange={getSelectHandler(`${record.id}:smelterLookup`)}
                  options={
                    smelterLookupMeta
                      ? [
                          {
                            value: smelterLookupMeta.notListed,
                            label: smelterLookupMeta.notListed,
                          },
                          {
                            value: smelterLookupMeta.notYetIdentified,
                            label: smelterLookupMeta.notYetIdentified,
                          },
                        ]
                      : []
                  }
                  allowClear
                  disabled={componentDisabled}
                  {...getReadonlyTextControlProps({
                    value,
                    placeholder: t('placeholders.smelterName'),
                    disabled: componentDisabled,
                    className: 'w-full',
                  })}
                />
              </Flex>
            ),
            componentDisabled,
          ),
      })
    }

    // Smelter Number input column (if configured) - keep after metal + lookup/name
    if (config.hasIdColumn) {
      columns.push({
        title: resolveColumnTitle('smelterNumberInput'),
        dataIndex: 'smelterNumber',
        key: 'smelterNumber',
        width: 180,
        fixed: 'left',
        render: (value: string, record: SmelterRow) => {
          const notListed = isNotListed(record.smelterLookup)
          const notYetIdentified = isNotYetIdentified(record.smelterLookup)
          const fromLookup = isFromLookup(record.smelterLookup)
          const disableAfterExternalPick = shouldDisableSmelterFieldsAfterExternalPick({
            useExternalLookup,
            row: record,
            fromLookup,
            notListed,
            notYetIdentified,
          })
          const disabled =
            componentDisabled || disableAfterExternalPick || numberLookupRowId === record.id
          return (
            <Input
              value={value || undefined}
              onChange={getInputHandler(`${record.id}:smelterNumber`)}
              onBlur={() => {
                void handleSmelterNumberLookup(record.id)
              }}
              onPressEnter={(event) => {
                event.currentTarget.blur()
              }}
              disabled={disabled}
              {...getReadonlyTextControlProps({
                value,
                placeholder: t('placeholders.smelterNumberInput'),
                disabled,
                className: 'font-mono text-xs',
              })}
            />
          )
        },
      })
    }

    columns.push({
      title: resolveColumnTitle('smelterName'),
      dataIndex: 'smelterName',
      key: 'smelterName',
      width: 200,
      render: (value: string, record: SmelterRow) => {
        const notListed = isNotListed(record.smelterLookup) && notListedRequiresNameCountry
        const notYetIdentified = isNotYetIdentified(record.smelterLookup)
        const fromLookup = isFromLookup(record.smelterLookup)
        const disableAfterExternalPick = shouldDisableSmelterFieldsAfterExternalPick({
          useExternalLookup,
          row: record,
          fromLookup,
          notListed,
          notYetIdentified,
        })
        const placeholder = notListed
          ? t('placeholders.smelterNameRequired')
          : notYetIdentified
            ? t('placeholders.smelterNameOptional')
            : t('placeholders.smelterName')
        return wrapRequired(
          notListed,
          <Input
            value={value || undefined}
            onChange={getInputHandler(`${record.id}:smelterName`)}
            disabled={componentDisabled || disableAfterExternalPick}
            {...getReadonlyTextControlProps({
              value,
              placeholder,
              disabled: componentDisabled || disableAfterExternalPick,
            })}
          />,
          componentDisabled,
        )
      },
    })

    columns.push(
      {
        title: resolveColumnTitle('smelterCountry'),
        dataIndex: 'smelterCountry',
        key: 'smelterCountry',
        width: 180,
        render: (value: string, record: SmelterRow) => {
          const notListed = isNotListed(record.smelterLookup) && notListedRequiresNameCountry
          const notYetIdentified = isNotYetIdentified(record.smelterLookup)
          const fromLookup = isFromLookup(record.smelterLookup)
          const disableAfterExternalPick = shouldDisableSmelterFieldsAfterExternalPick({
            useExternalLookup,
            row: record,
            fromLookup,
            notListed,
            notYetIdentified,
          })
          const placeholder = notListed
            ? t('placeholders.smelterCountryRequired')
            : t('placeholders.smelterCountry')
          const showHint = notYetIdentified && showNotYetIdentifiedCountryHint
          return wrapRequired(
            notListed,
            <Flex vertical gap={4}>
              <Select
                value={value || undefined}
                onChange={getSelectHandler(`${record.id}:smelterCountry`)}
                options={countryOptions}
                showSearch
                filterOption={filterOptionByLabel}
                disabled={componentDisabled || disableAfterExternalPick}
                {...getReadonlyTextControlProps({
                  value,
                  placeholder,
                  disabled: componentDisabled || disableAfterExternalPick,
                  className: 'w-full',
                })}
              />
              {showHint && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('hints.smelterNotYetIdentifiedCountry')}
                </Typography.Text>
              )}
            </Flex>,
            componentDisabled,
          )
        },
      },
      {
        title: resolveColumnTitle('smelterIdentification'),
        dataIndex: 'smelterIdentification',
        key: 'smelterIdentification',
        width: 180,
        render: (value: string, record: SmelterRow) => {
          const disabled = isSmelterBaseFieldDisabled(record)
          return (
            <Input
              value={value || undefined}
              onChange={getInputHandler(`${record.id}:smelterIdentification`)}
              disabled={disabled}
              {...getReadonlyTextControlProps({
                value,
                placeholder: t('placeholders.smelterIdentification'),
                disabled,
              })}
            />
          )
        },
      },
      {
        title: resolveColumnTitle('smelterStreet'),
        dataIndex: 'smelterStreet',
        key: 'smelterStreet',
        width: 200,
        render: (value: string, record: SmelterRow) => {
          const disabled = isSmelterBaseFieldDisabled(record)
          return (
            <Input
              value={value || undefined}
              onChange={getInputHandler(`${record.id}:smelterStreet`)}
              disabled={disabled}
              {...getReadonlyTextControlProps({
                value,
                placeholder: t('placeholders.smelterStreet'),
                disabled,
              })}
            />
          )
        },
      },
      {
        title: resolveColumnTitle('smelterCity'),
        dataIndex: 'smelterCity',
        key: 'smelterCity',
        width: 160,
        render: (value: string, record: SmelterRow) => {
          const disabled = isSmelterBaseFieldDisabled(record)
          return (
            <Input
              value={value || undefined}
              onChange={getInputHandler(`${record.id}:smelterCity`)}
              disabled={disabled}
              {...getReadonlyTextControlProps({
                value,
                placeholder: t('placeholders.smelterCity'),
                disabled,
              })}
            />
          )
        },
      },
      {
        title: resolveColumnTitle('smelterState'),
        dataIndex: 'smelterState',
        key: 'smelterState',
        width: 170,
        render: (value: string, record: SmelterRow) => {
          const disabled = isSmelterBaseFieldDisabled(record)
          return (
            <Input
              value={value || undefined}
              onChange={getInputHandler(`${record.id}:smelterState`)}
              disabled={disabled}
              {...getReadonlyTextControlProps({
                value,
                placeholder: t('placeholders.smelterState'),
                disabled,
              })}
            />
          )
        },
      },
      {
        title: resolveColumnTitle('smelterContactName'),
        dataIndex: 'smelterContactName',
        key: 'smelterContactName',
        width: 180,
        render: (value: string, record: SmelterRow) => (
          <Input
            value={value || undefined}
            onChange={getInputHandler(`${record.id}:smelterContactName`)}
            disabled={componentDisabled}
            {...getReadonlyTextControlProps({
              value,
              placeholder: t('placeholders.smelterContactName'),
              disabled: componentDisabled,
            })}
          />
        ),
      },
      {
        title: resolveColumnTitle('smelterContactEmail'),
        dataIndex: 'smelterContactEmail',
        key: 'smelterContactEmail',
        width: 200,
        render: (value: string, record: SmelterRow) => (
          <Input
            value={value || undefined}
            onChange={getInputHandler(`${record.id}:smelterContactEmail`)}
            disabled={componentDisabled}
            {...getReadonlyTextControlProps({
              value,
              placeholder: t('placeholders.smelterContactEmail'),
              disabled: componentDisabled,
            })}
          />
        ),
      },
      {
        title: resolveColumnTitle('proposedNextSteps'),
        dataIndex: 'proposedNextSteps',
        key: 'proposedNextSteps',
        width: 200,
        render: (value: string, record: SmelterRow) => (
          <Input
            value={value || undefined}
            onChange={getInputHandler(`${record.id}:proposedNextSteps`)}
            disabled={componentDisabled}
            {...getReadonlyTextControlProps({
              value,
              placeholder: t('placeholders.smelterNextSteps'),
              disabled: componentDisabled,
            })}
          />
        ),
      },
      {
        title: resolveColumnTitle('mineName'),
        dataIndex: 'mineName',
        key: 'mineName',
        width: 180,
        render: (value: string, record: SmelterRow) => (
          <Input
            value={value || undefined}
            onChange={getInputHandler(`${record.id}:mineName`)}
            disabled={componentDisabled}
            {...getReadonlyTextControlProps({
              value,
              placeholder: t('placeholders.smelterMineName'),
              disabled: componentDisabled,
            })}
          />
        ),
      },
      {
        title: resolveColumnTitle('mineCountry'),
        dataIndex: 'mineCountry',
        key: 'mineCountry',
        width: 240,
        render: (value: string, record: SmelterRow) => (
          <AutoComplete
            value={value || undefined}
            onChange={getSelectHandler(`${record.id}:mineCountry`)}
            options={countryOptions}
            allowClear
            disabled={componentDisabled}
            {...getReadonlyTextControlProps({
              value,
              placeholder: t('placeholders.smelterMineCountry'),
              disabled: componentDisabled,
              className: 'w-full',
            })}
          />
        ),
      },
      {
        title: resolveColumnTitle('recycledScrap'),
        dataIndex: 'recycledScrap',
        key: 'recycledScrap',
        width: 260,
        render: (value: string, record: SmelterRow) => (
          <Select
            value={value || undefined}
            onChange={getSelectHandler(`${record.id}:recycledScrap`)}
            options={recycledScrapOptions}
            disabled={componentDisabled}
            {...getReadonlyTextControlProps({
              value,
              placeholder: t('placeholders.select'),
              disabled: componentDisabled,
              className: 'w-full',
            })}
          />
        ),
      },
      {
        title: resolveColumnTitle('comments'),
        dataIndex: 'comments',
        key: 'comments',
        width: 200,
        render: (value: string, record: SmelterRow) => (
          <Input
            value={value || undefined}
            onChange={getInputHandler(`${record.id}:comments`)}
            disabled={componentDisabled}
            {...getReadonlyTextControlProps({
              value,
              placeholder: t('placeholders.smelterComments'),
              disabled: componentDisabled,
            })}
          />
        ),
      },
    )

    if (config.hasCombinedColumn) {
      columns.push(
        {
          title: t('tables.combinedMetal'),
          dataIndex: 'combinedMetal',
          key: 'combinedMetal',
          width: 180,
          render: (value: string, record: SmelterRow) => (
            <Select
              value={value || undefined}
              onChange={getSelectHandler(`${record.id}:combinedMetal`)}
              options={metalOptions}
              disabled={componentDisabled}
              {...getReadonlyTextControlProps({
                value,
                placeholder: t('placeholders.smelterCombinedMetal'),
                disabled: componentDisabled,
                className: 'w-full',
              })}
            />
          ),
        },
        {
          title: t('tables.combinedSmelter'),
          dataIndex: 'combinedSmelter',
          key: 'combinedSmelter',
          width: 220,
          render: (value: string, record: SmelterRow) => (
            <Input
              value={value || undefined}
              onChange={getInputHandler(`${record.id}:combinedSmelter`)}
              disabled={componentDisabled}
              {...getReadonlyTextControlProps({
                value,
                placeholder: t('placeholders.smelterCombinedSmelter'),
                disabled: componentDisabled,
              })}
            />
          ),
        },
      )
    }

    const orderedColumns = headerProfile.columns
      .map((columnId) => {
        const columnKey = SMELTER_COLUMN_KEY_BY_ID[columnId]
        return columns.find((column) => column.key === columnKey)
      })
      .filter((column): column is NonNullable<(typeof columns)[number]> => Boolean(column))

    if (!showEditableActions) {
      return orderedColumns
    }

    return [
      ...orderedColumns,
      {
        title: '',
        key: 'actions',
        width: 60,
        fixed: 'right',
        render: (_: unknown, record: SmelterRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={getRemoveHandler(record.id)}
          />
        ),
      },
    ]
  }, [
    headerProfile,
    config,
    componentDisabled,
    countryOptions,
    handleCellChange,
    handleExternalPickForRow,
    handleRemoveRow,
    integration,
    isSmelterBaseFieldDisabled,
    metalOptions,
    notListedRequiresNameCountry,
    rowPickingId,
    showLoadingIndicator,
    showEditableActions,
    smelterLookupMeta,
    smelterLookupMode,
    t,
    useExternalLookup,
    yesNoUnknownOptions,
    yesNoOptions,
    recycledScrapOptions,
    resolveColumnTitle,
  ])

  const emptyLocale = {
    emptyText: (
      <Flex vertical align="center" gap={16} className="py-8">
        <Typography.Text type="secondary" className="text-sm">
          {t('tables.noData')}
        </Typography.Text>
        <Flex align="center" gap={8}>
          {showEditableActions && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRow}>
              {t('actions.addRow')}
            </Button>
          )}
        </Flex>
      </Flex>
    ),
  }

  const getSmelterRowClassName = useMemoizedFn((record: SmelterRow, index: number) => {
    const builtInClassName = isNotListed(record.smelterLookup) ? CUSTOM_SMELTER_ROW_CLASS_NAME : ''
    const integrationClassName = integration?.rowClassName?.(record, index) ?? ''
    return [builtInClassName, integrationClassName].filter(Boolean).join(' ')
  })

  return (
    <Card
      title={
        <Flex vertical gap={8} style={{ width: '100%' }}>
          <Flex align="center" justify="space-between">
            <Flex align="center" gap={8}>
              <Typography.Title level={5} style={{ margin: 0 }}>
                {t('tabs.smelterList')}
              </Typography.Title>
              <Tag color="blue">{t('badges.recordCount', { count: rows.length })}</Tag>
            </Flex>
            <Flex align="center" gap={8}>
              {showEditableActions && (
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRow}>
                  {t('actions.addRow')}
                </Button>
              )}
            </Flex>
          </Flex>
          {showEditableActions && validSelectedRowKeys.length > 0 && (
            <Flex
              align="center"
              gap={12}
              style={{
                padding: '8px 12px',
                background: 'var(--ant-color-primary-bg)',
                borderRadius: 6,
              }}
            >
              <Typography.Text strong>
                {t('selection.selected', { count: validSelectedRowKeys.length })}
              </Typography.Text>
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={handleBatchDelete}
              >
                {t('actions.batchDelete')}
              </Button>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={handleCancelSelection}
              >
                {t('actions.cancelSelection')}
              </Button>
            </Flex>
          )}
        </Flex>
      }
    >
      <Table
        className="smelter-list-table"
        columns={columns}
        dataSource={rows}
        rowKey="id"
        rowClassName={getSmelterRowClassName}
        rowSelection={showEditableActions ? rowSelection : undefined}
        pagination={false}
        scroll={{ x: 'max-content', y: rows.length > 20 ? 600 : undefined }}
        virtual={rows.length > 50}
        bordered
        locale={emptyLocale}
      />
    </Card>
  )
})
